import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch, AppThunk } from "@/app/types";
import { fetchBundles } from "@/api/bundles";
import { BundleDirectoryItem, FetchBundlesParams } from "@/api/bundles/types";

export interface FilterState {
  search: string;
  contentTypes: string[];
}

export interface PaginationState {
  page: number;
  limit: number;
  totalItems: number;
  totalPages: number;
  hasMore: boolean;
}

export interface BundleDirectoryState {
  bundles: BundleDirectoryItem[];
  filters: FilterState;
  pagination: PaginationState;
  loading: boolean;
  error: string | null;
  scrollPosition: number;
  lastFetchParams: FetchBundlesParams | null;
}

const initialState: BundleDirectoryState = {
  bundles: [],
  filters: {
    search: "",
    contentTypes: [],
  },
  pagination: {
    page: 1,
    limit: 20,
    totalItems: 0,
    totalPages: 0,
    hasMore: true,
  },
  loading: false,
  error: null,
  scrollPosition: 0,
  lastFetchParams: null,
};

const bundleDirectorySlice = createSlice({
  name: "bundleDirectory",
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<Partial<FilterState>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    setBundles: (state, action: PayloadAction<BundleDirectoryItem[]>) => {
      state.bundles = action.payload;
    },
    appendBundles: (state, action: PayloadAction<BundleDirectoryItem[]>) => {
      state.bundles = [...state.bundles, ...action.payload];
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setPagination: (state, action: PayloadAction<Partial<PaginationState>>) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    setScrollPosition: (state, action: PayloadAction<number>) => {
      state.scrollPosition = action.payload;
    },
    setLastFetchParams: (
      state,
      action: PayloadAction<FetchBundlesParams | null>,
    ) => {
      state.lastFetchParams = action.payload;
    },
    resetFilters: (state) => {
      state.filters = initialState.filters;
      state.pagination.page = 1;
      state.pagination.hasMore = true;
    },
    resetState: (state) => {
      Object.assign(state, initialState);
    },
    hydrateFromCache: (
      state,
      action: PayloadAction<Partial<BundleDirectoryState>>,
    ) => {
      const cachedState = action.payload;

      if (cachedState.bundles) {
        state.bundles = cachedState.bundles;
      }
      if (cachedState.filters) {
        state.filters = cachedState.filters;
      }
      if (cachedState.pagination) {
        state.pagination = cachedState.pagination;
      }
      if (cachedState.scrollPosition !== undefined) {
        state.scrollPosition = cachedState.scrollPosition;
      }
      if (cachedState.lastFetchParams) {
        state.lastFetchParams = cachedState.lastFetchParams;
      }
    },
  },
});

/**
 * Async thunk to fetch bundles with filters
 * Replaces the current bundles list (used for initial load and filter changes)
 */
export const loadBundles =
  (params?: Partial<FetchBundlesParams>): AppThunk =>
  async (dispatch: AppDispatch, getState) => {
    try {
      dispatch(setLoading(true));
      dispatch(setError(null));

      const state = getState();
      const { filters, pagination } = state.bundleDirectory;

      const apiParams: FetchBundlesParams = {
        page: params?.page ?? 1,
        limit: params?.limit ?? pagination.limit,
        search: filters.search || undefined,
        contentTypes:
          filters.contentTypes.length > 0 ? filters.contentTypes : undefined,
        ...params,
      };

      const response = await fetchBundles(apiParams);

      dispatch(setBundles(response.items));
      dispatch(
        setPagination({
          page: response.page,
          limit: response.limit,
          totalItems: response.total,
          totalPages: response.totalPages,
          hasMore: response.page < response.totalPages,
        }),
      );
      dispatch(setLastFetchParams(apiParams));
    } catch (error) {
      console.error("Error fetching bundles:", error);
      dispatch(
        setError(
          error instanceof Error ? error.message : "Failed to fetch bundles",
        ),
      );
      dispatch(
        setPagination({
          hasMore: false,
        }),
      );
    } finally {
      dispatch(setLoading(false));
    }
  };

/**
 * Async thunk to fetch more bundles for infinite scroll
 * Appends to the existing bundles list
 */
export const loadMoreBundles =
  (): AppThunk => async (dispatch: AppDispatch, getState) => {
    try {
      const state = getState();
      const { filters, pagination, loading } = state.bundleDirectory;

      // Prevent duplicate requests
      if (loading || !pagination.hasMore) {
        return;
      }

      dispatch(setLoading(true));
      dispatch(setError(null));

      const nextPage = pagination.page + 1;

      const apiParams: FetchBundlesParams = {
        page: nextPage,
        limit: pagination.limit,
        search: filters.search || undefined,
        contentTypes:
          filters.contentTypes.length > 0 ? filters.contentTypes : undefined,
      };

      const response = await fetchBundles(apiParams);

      dispatch(appendBundles(response.items));
      dispatch(
        setPagination({
          page: response.page,
          totalItems: response.total,
          totalPages: response.totalPages,
          hasMore: response.page < response.totalPages,
        }),
      );
      dispatch(setLastFetchParams(apiParams));
    } catch (error) {
      console.error("Error loading more bundles:", error);
      dispatch(
        setError(
          error instanceof Error
            ? error.message
            : "Failed to load more bundles",
        ),
      );
      dispatch(
        setPagination({
          hasMore: false,
        }),
      );
    } finally {
      dispatch(setLoading(false));
    }
  };

export const {
  setFilters,
  setBundles,
  appendBundles,
  setLoading,
  setError,
  setPagination,
  setScrollPosition,
  setLastFetchParams,
  resetFilters,
  resetState,
  hydrateFromCache,
} = bundleDirectorySlice.actions;

export default bundleDirectorySlice.reducer;
